import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import ApiServices from '../../shared/ApiServices/ApiServices';
import LabServices from '../../shared/ApiServices/LabServices';
import { V2Button, V2Card, V2Modal, V2Badge } from '../../shared/custom-ui/v2-components';
import { SchedulingSkeleton } from '../../shared/custom-ui';
import { toast } from 'react-toastify';
import AddScheduling from '../patients-history/actions/appointment/addScheduling';
import { PlaceOrderModal } from '../Lab/OrderManagement';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../scheduling/scheduling.css';
import { buildPatientDisplayName } from '../../shared/utils/nameUtils';
import ModernPageHeader from '../WorkListTable/components/ModernPageHeader';
import {
    CalendarDays, X, FileText, FlaskConical,
    CheckCircle2, Search, Loader2, CalendarPlus,
    Link2, Info,
} from 'lucide-react';

const localizer = momentLocalizer(moment);
const BigCalendar = Calendar as any;

type RbcView = 'month' | 'week' | 'day';

/* ─── Tiny read-only row ──────────────────────────────────────── */
const DetailItem = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--v2-text-muted)' }}>{label}</span>
        <span className="text-[13px] font-semibold break-words" style={{ color: 'var(--v2-text-heading)' }}>{value ?? '—'}</span>
    </div>
);

/* ─── Event chip ─────────────────────────────────────────────── */
const EventChip: React.FC<{ event: any }> = ({ event }) => {
    const status = String(event.Status || '').toLowerCase();
    return (
        <div className={`rbc-chip ${status ? `rbc-chip--${status}` : ''}`} title={event.title}>
            <span className="rbc-chip__title">{event.title}</span>
        </div>
    );
};

/* ─── Status badge ────────────────────────────────────────────── */
const statusBadge = (status: string) => {
    const s = (status || '').toUpperCase();
    const variant =
        s === 'SCHEDULED' ? 'info'
            : s === 'COMPLETED' ? 'success'
                : s === 'CANCELLED' ? 'danger'
                    : 'default';
    return <V2Badge variant={variant as any} size="small">{s || 'UNKNOWN'}</V2Badge>;
};

/* ─── Pathology-only Edit Modal ───────────────────────────────── */
interface PathologyEditModalProps {
    event: any;
    onClose: () => void;
    onSuccess: () => void;
}

const PathologyEditModal: React.FC<PathologyEditModalProps> = ({ event, onClose, onSuccess }) => {
    const [dateTime, setDateTime] = useState('');
    const [note, setNote] = useState('');
    const [allTests, setAllTests] = useState<any[]>([]);
    const [testSearch, setTestSearch] = useState('');
    const [selectedTests, setSelectedTests] = useState<Array<{ testId: string; testName: string }>>([]);
    const [testsLoading, setTestsLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    /* Prefill from existing appointment */
    useEffect(() => {
        const raw = event.SK || event.Date || '';
        const local = raw ? moment.parseZone(raw).format('YYYY-MM-DDTHH:mm') : '';
        setDateTime(local);
        setNote(event.Note ?? event.note ?? '');
        if (Array.isArray(event.Tests)) {
            setSelectedTests(event.Tests.map((t: any) => ({ testId: t.testId, testName: t.testName })));
        }
    }, [event]);

    /* Fetch lab tests catalog */
    useEffect(() => {
        setTestsLoading(true);
        LabServices.getLabTests()
            .then(res => {
                const list = res?.tests ?? res?.items ?? (Array.isArray(res) ? res : []);
                setAllTests(list);
            })
            .catch(() => setAllTests([]))
            .finally(() => setTestsLoading(false));
    }, []);

    const filteredTests = testSearch.trim()
        ? allTests.filter(t =>
            (t.testName ?? '').toLowerCase().includes(testSearch.toLowerCase()) ||
            (t.testCode ?? '').toLowerCase().includes(testSearch.toLowerCase())
        )
        : allTests;

    const toggleTest = (t: any) => {
        const id = t.PK ?? t.testId ?? t.id ?? '';
        const name = t.testName ?? t.name ?? '';
        setSelectedTests(prev =>
            prev.find(s => s.testId === id)
                ? prev.filter(s => s.testId !== id)
                : [...prev, { testId: id, testName: name }]
        );
    };

    const handleSubmit = async () => {
        if (!dateTime) { toast.error('Please select a new appointment date and time.'); return; }
        if (selectedTests.length === 0) { toast.error('Please select at least one lab test.'); return; }
        setSaving(true);
        try {
            const oldDateTime = event.SK || event.Date || '';
            // Convert the local datetime-local value to a full ISO string with TZ offset
            const newDateTime = moment(dateTime).format();   // e.g. "2026-07-20T10:00:00+05:30"
            const res: any = await ApiServices.modifyAppointment({
                patientId: event.PK,
                oldAppointmentDateTime: oldDateTime,
                newAppointmentDateTime: newDateTime,
                tests: selectedTests,
                data: [{ note }],
                note,
            });
            toast.success(res?.message ?? 'Appointment updated successfully');
            onSuccess();
            onClose();
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? err?.message ?? 'Failed to update appointment');
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[400] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="w-full max-w-lg rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

                {/* Header */}
                <div className="relative flex items-center gap-3 px-5 py-4 border-b border-[var(--v2-border-subtle)] overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: 'linear-gradient(90deg,#1D6FB8 0%,rgba(29,111,184,0.4) 60%,transparent 100%)' }} />
                    <div aria-hidden className="absolute inset-0" style={{ background: 'var(--v2-bg-hero)' }} />

                    <span className="relative flex items-center justify-center w-8 h-8 rounded-xl flex-shrink-0" style={{ background: 'rgba(29,111,184,0.10)', color: '#1D6FB8' }}>
                        <CalendarPlus className="w-4 h-4" />
                    </span>
                    <div className="relative flex-1 min-w-0">
                        <h2 className="text-[14px] font-bold text-[var(--v2-text-heading)] m-0 leading-snug">Update Pathology Appointment</h2>
                        <p className="text-[11px] text-[var(--v2-text-muted)] m-0 truncate">Reschedule or change tests for this appointment</p>
                    </div>
                    <button type="button" onClick={onClose} className="relative w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer border-0 bg-transparent text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] hover:bg-[var(--v2-bg-hover)] transition-colors">
                        <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">

                    {/* Patient chip */}
                    <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">Patient</span>
                        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl border" style={{ background: 'var(--v2-bg-surface)', borderColor: 'var(--v2-border-card)' }}>
                            <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-black text-white" style={{ background: '#1D6FB8' }}>
                                {(event.PatientName || event.title || 'P').charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <p className="text-[13px] font-bold text-[var(--v2-text-primary)] m-0 truncate">{event.PatientName ?? event.title ?? '—'}</p>
                                <p className="text-[10px] text-[var(--v2-text-muted)] m-0 truncate font-mono">MRN: {event.MRN}</p>
                            </div>
                        </div>
                    </div>

                    {/* New Date & Time */}
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">
                            New Appointment Date & Time <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="datetime-local"
                            value={dateTime}
                            onChange={e => setDateTime(e.target.value)}
                            min={new Date().toISOString().slice(0, 16)}
                            className="w-full rounded-xl border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] px-3 py-2 text-[13px] text-[var(--v2-text-primary)] outline-none transition-colors"
                        />
                    </div>

                    {/* Note */}
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">Note</label>
                        <textarea
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder="E.g. Rescheduled. Added thyroid profile…"
                            rows={2}
                            className="w-full rounded-xl border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] px-3 py-2 text-[13px] text-[var(--v2-text-primary)] outline-none transition-colors resize-none"
                        />
                    </div>

                    {/* Lab Tests */}
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">
                                Lab Tests <span className="text-red-500">*</span>
                            </span>
                            {selectedTests.length > 0 && (
                                <span className="text-[10px] font-black px-2 py-0.5 rounded-full text-white" style={{ background: '#059669' }}>
                                    {selectedTests.length} selected
                                </span>
                            )}
                        </div>

                        {/* Selected chips */}
                        {selectedTests.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {selectedTests.map(t => (
                                    <span
                                        key={t.testId}
                                        className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-[11px] font-semibold"
                                        style={{ background: 'rgba(5,150,105,0.10)', color: '#059669', border: '1px solid rgba(5,150,105,0.25)' }}
                                    >
                                        {t.testName}
                                        <button
                                            type="button"
                                            onClick={() => setSelectedTests(prev => prev.filter(s => s.testId !== t.testId))}
                                            className="w-4 h-4 flex items-center justify-center rounded-full cursor-pointer border-0 bg-transparent hover:bg-[rgba(5,150,105,0.2)]"
                                            style={{ color: '#059669' }}
                                        >
                                            <X size={10} strokeWidth={2.5} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-[13px] h-[13px] pointer-events-none" style={{ color: 'var(--v2-text-muted)' }} />
                            <input
                                type="text"
                                placeholder="Search tests by name or code…"
                                value={testSearch}
                                onChange={e => setTestSearch(e.target.value)}
                                className="w-full pl-8 pr-3 py-2 rounded-xl border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] text-[13px] text-[var(--v2-text-primary)] outline-none transition-colors"
                            />
                        </div>

                        {/* Test list */}
                        <div
                            className="rounded-xl border overflow-hidden overflow-y-auto"
                            style={{ maxHeight: 200, borderColor: 'var(--v2-border-card)', background: 'var(--v2-bg-surface)' }}
                        >
                            {testsLoading ? (
                                <div className="flex items-center justify-center h-[80px] gap-2 text-[var(--v2-text-muted)]">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-[12px]">Loading tests…</span>
                                </div>
                            ) : filteredTests.length === 0 ? (
                                <div className="px-4 py-6 text-center text-[12px]" style={{ color: 'var(--v2-text-muted)' }}>
                                    {allTests.length === 0 ? 'No lab tests in catalog.' : 'No tests match your search.'}
                                </div>
                            ) : (
                                filteredTests.map((t: any) => {
                                    const id = t.PK ?? t.testId ?? t.id ?? '';
                                    const name = t.testName ?? t.name ?? '';
                                    const price = t.price ?? '';
                                    const cat = t.category ?? '';
                                    const isSel = selectedTests.some(s => s.testId === id);
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => toggleTest(t)}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 border-b cursor-pointer text-left bg-transparent transition-colors hover:bg-[var(--v2-bg-card)]"
                                            style={{ borderColor: 'var(--v2-border-subtle)', background: isSel ? 'rgba(5,150,105,0.06)' : undefined }}
                                        >
                                            <span
                                                className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                                                style={{ background: isSel ? '#059669' : 'var(--v2-bg-card)', border: `1.5px solid ${isSel ? '#059669' : 'var(--v2-border-card)'}` }}
                                            >
                                                {isSel && <CheckCircle2 size={11} color="white" strokeWidth={2.5} />}
                                            </span>
                                            <span className="flex-1 min-w-0">
                                                <p className="text-[12px] font-semibold m-0 truncate" style={{ color: 'var(--v2-text-primary)' }}>{name}</p>
                                                {cat && <p className="text-[10px] m-0 mt-0.5" style={{ color: 'var(--v2-text-muted)' }}>{cat}</p>}
                                            </span>
                                            {price && <span className="text-[11px] font-bold flex-shrink-0" style={{ color: '#059669' }}>₹{price}</span>}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'var(--v2-border-subtle)', background: 'var(--v2-bg-surface)' }}>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] text-[13px] font-semibold text-[var(--v2-text-secondary)] cursor-pointer hover:bg-[var(--v2-bg-hover)] disabled:opacity-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-bold text-white cursor-pointer disabled:opacity-60 transition-all"
                        style={{ background: saving ? '#1D6FB8aa' : 'linear-gradient(to bottom,#1D6FB8,#155fa0)', boxShadow: '0 2px 8px rgba(29,111,184,0.25)' }}
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
                        {saving ? 'Updating…' : 'Update Appointment'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

/* ─── Main Scheduling Page ────────────────────────────────────── */
export default function Scheduling() {
    const [calendarDate, setCalendarDate] = useState<Date>(new Date());
    const [calendarView, setCalendarView] = useState<RbcView>('month');
    const [events, setEvents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [apptTypeFilter, setApptTypeFilter] = useState<'all' | 'radiology' | 'pathology'>('all');

    const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [isAddSchedulingOpen, setIsAddSchedulingOpen] = useState(false);
    const [isPathologyEditOpen, setIsPathologyEditOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showLabOrder, setShowLabOrder] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        fetchAppointments(calendarDate, calendarView, apptTypeFilter);
    }, [calendarDate, calendarView, apptTypeFilter]);

    useEffect(() => {
        if ((location as any).state?.newEvent) {
            setEvents((prev) => [...prev, (location as any).state.newEvent]);
        }
    }, [location]);

    const getRange = (date: Date, view: RbcView) => {
        if (view === 'month') return { start: moment(date).startOf('month'), end: moment(date).endOf('month') };
        if (view === 'week') return { start: moment(date).startOf('week'), end: moment(date).endOf('week') };
        return { start: moment(date).startOf('day'), end: moment(date).endOf('day') };
    };

    const fetchAppointments = async (
        date: Date = new Date(),
        view: RbcView = 'month',
        typeFilter: 'all' | 'radiology' | 'pathology' = 'all',
    ) => {
        setLoading(true);
        try {
            const { start, end } = getRange(date, view);
            const params: Record<string, any> = {
                start_date: start.format('YYYY-MM-DD'),
                end_date: end.format('YYYY-MM-DD'),
                limit: 1000,
            };
            if (typeFilter !== 'all') params.appointmentType = typeFilter;
            const response = await ApiServices.getAppointment(params);
            const appointments = (response.items || []).map((appt: any) => {
                const m = moment.parseZone(appt.SK);
                const startDate = new Date(m.format('YYYY-MM-DDTHH:mm:ss'));
                const endDate = new Date(m.clone().add(10, 'minutes').format('YYYY-MM-DDTHH:mm:ss'));
                return { title: buildPatientDisplayName(appt), start: startDate, end: endDate, ...appt };
            });
            setEvents(appointments);
        } catch (err) {
            console.error('Error fetching appointments:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectEvent = useCallback((event: any) => {
        setSelectedEvent(event);
        setIsDetailsOpen(true);
    }, []);

    const isPathologyAppt = (ev: any) =>
        String(ev?.AppointmentType ?? ev?.appointmentType ?? '').toLowerCase() === 'pathology';

    const handleModifyAppointment = () => {
        setIsDetailsOpen(false);
        if (isPathologyAppt(selectedEvent)) {
            setIsPathologyEditOpen(true);
        } else {
            setIsAddSchedulingOpen(true);
        }
    };

    const handleDeleteAppointment = async () => {
        if (!selectedEvent || isDeleting) return;
        setIsDeleting(true);
        try {
            await ApiServices.deleteAppointment({
                PatientID: selectedEvent.PK,
                AppointmentDateTime: selectedEvent.SK,
            });
            setEvents((prev) => prev.filter((e) => e.PK !== selectedEvent.PK || e.SK !== selectedEvent.SK));
            toast.success('Appointment deleted successfully!');
            await fetchAppointments(calendarDate, calendarView);
            setIsDetailsOpen(false);
        } catch {
            toast.error('Failed to delete the appointment. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    /** Filter chip row — All / Radiology / Pathology */
    const FilterChips = useMemo(() => {
        const chips: Array<{ key: 'all' | 'radiology' | 'pathology'; label: string; color: string }> = [
            { key: 'all', label: 'All', color: 'var(--v2-text-secondary)' },
            { key: 'radiology', label: 'Radiology', color: '#1D6FB8' },
            { key: 'pathology', label: 'Pathology', color: '#7C3AED' },
        ];
        return (
            <div className="inline-flex items-center gap-1 rounded-xl border p-1" style={{ borderColor: 'var(--v2-border-subtle)', background: 'var(--v2-bg-card)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                {chips.map(c => (
                    <button
                        key={c.key}
                        type="button"
                        onClick={() => setApptTypeFilter(c.key)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all duration-150 cursor-pointer"
                        style={apptTypeFilter === c.key
                            ? { background: c.color + '18', color: c.color, border: `1.5px solid ${c.color}40` }
                            : { background: 'transparent', color: 'var(--v2-text-muted)', border: '1.5px solid transparent' }
                        }
                        aria-pressed={apptTypeFilter === c.key}
                    >
                        {c.key !== 'all' && (
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: c.color }} />
                        )}
                        {c.label}
                    </button>
                ))}
            </div>
        );
    }, [apptTypeFilter]);

    /* View Switch */
    const ViewSwitch = useMemo(
        () => (
            <div
                className="inline-flex items-center gap-1 rounded-xl border p-1"
                style={{
                    borderColor: 'var(--v2-border-subtle)',
                    background: 'var(--v2-bg-card)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                }}
            >
                {(['month', 'week', 'day'] as RbcView[]).map((v) => (
                    <V2Button
                        key={v}
                        size="small"
                        variant={calendarView === v ? 'primary' : 'ghost'}
                        onClick={() => setCalendarView(v)}
                    >
                        {v[0].toUpperCase() + v.slice(1)}
                    </V2Button>
                ))}
            </div>
        ),
        [calendarView]
    );

    return (
        <div className="relative">
            {/* ══════ HEADER ══════ */}
            <ModernPageHeader
                title="Scheduling"
                subtitle="Browse appointments by month, week, or day"
                icon={<CalendarDays className="w-[15px] h-[15px]" />}
                actions={
                    <>
                        {FilterChips}
                        {ViewSwitch}
                        <V2Button
                            variant="primary"
                            size="small"
                            leftIcon={<svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>}
                            onClick={() => navigate('/scheduling/add-order')}
                            aria-label="Add Order"
                        >
                            Add Order
                        </V2Button>
                    </>
                }
            />

            {/* ══════ CALENDAR ══════ */}
            {loading ? (
                <SchedulingSkeleton />
            ) : (
                <V2Card noPadding>
                    {/* Calendar toolbar */}
                    <div
                        className="flex items-center justify-between gap-3 border-b"
                        style={{
                            padding: 'var(--v2-space-3) var(--v2-space-4)',
                            borderColor: 'var(--v2-border-subtle)',
                            background: 'var(--v2-bg-surface)',
                        }}
                    >
                        <div className="flex items-center gap-2">
                            <V2Button
                                variant="ghost"
                                size="small"
                                onClick={() =>
                                    setCalendarDate((prev) =>
                                        moment(prev).subtract(1, calendarView === 'month' ? 'month' : calendarView === 'week' ? 'week' : 'day').toDate()
                                    )
                                }
                            >
                                ‹
                            </V2Button>
                            <V2Button variant="ghost" size="small" onClick={() => setCalendarDate(new Date())}>
                                Today
                            </V2Button>
                            <V2Button
                                variant="ghost"
                                size="small"
                                onClick={() =>
                                    setCalendarDate((prev) =>
                                        moment(prev).add(1, calendarView === 'month' ? 'month' : calendarView === 'week' ? 'week' : 'day').toDate()
                                    )
                                }
                            >
                                ›
                            </V2Button>
                        </div>
                        <div className="text-sm font-semibold" style={{ color: 'var(--v2-text-heading)' }}>
                            {moment(calendarDate).format(
                                calendarView === 'month' ? 'MMMM YYYY' : calendarView === 'week' ? '[Week of] MMM D, YYYY' : 'MMMM D, YYYY'
                            )}
                        </div>
                        <div className="text-[13px] font-medium" style={{ color: 'var(--v2-text-muted)' }}>
                            {events.length} events
                        </div>
                    </div>

                    <div className="relative overflow-y-auto">
                        <BigCalendar
                            localizer={localizer}
                            events={events}
                            startAccessor="start"
                            endAccessor="end"
                            titleAccessor="title"
                            onSelectEvent={handleSelectEvent}
                            onNavigate={(d: Date) => setCalendarDate(d)}
                            onView={(v: RbcView) => setCalendarView(v)}
                            view={calendarView}
                            date={calendarDate}
                            views={{ month: true, week: true, day: true }}
                            popup
                            selectable={false}
                            className="rbc-symbian-theme"
                            components={{ event: EventChip }}
                        />
                    </div>
                </V2Card>
            )}

            {/* ══════ VIEW APPOINTMENT MODAL ══════ */}
            <V2Modal
                isOpen={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
                size="large"
                title="Appointment Details"
                description="Review appointment details or modify / delete this appointment."
                showCloseButton
                footer={
                    <div className="flex justify-end gap-2.5">
                        <V2Button
                            variant="danger"
                            size="small"
                            leftIcon={<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>}
                            onClick={handleDeleteAppointment}
                            disabled={isDeleting}
                        >
                            {isDeleting ? 'Deleting…' : 'Delete'}
                        </V2Button>
                        <V2Button
                            variant="primary"
                            size="small"
                            leftIcon={<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>}
                            onClick={handleModifyAppointment}
                        >
                            Modify
                        </V2Button>
                    </div>
                }
            >
                {selectedEvent && (() => {
                    const apptType = String(selectedEvent.AppointmentType ?? selectedEvent.appointmentType ?? '').toLowerCase();
                    const isPathology = apptType === 'pathology';
                    const tests: Array<{ testId: string; testName: string }> = Array.isArray(selectedEvent.Tests) ? selectedEvent.Tests : [];
                    const linkedOrder = selectedEvent.LinkedOrderId ?? '';
                    const slideStatus = selectedEvent.SlideLibraryStatus ?? '';

                    return (
                        <div className="flex flex-col gap-5">

                            {/* ── Patient & Type ─────────────────────────────── */}
                            <section>
                                <p className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--v2-text-muted)' }}>
                                    Patient Information
                                </p>
                                <div className="rounded-xl border p-4 flex flex-col gap-4" style={{ borderColor: 'var(--v2-border-subtle)', background: 'var(--v2-bg-surface)' }}>

                                    {/* Patient avatar row */}
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-[13px] font-black text-white"
                                            style={{ background: '#1D6FB8' }}
                                        >
                                            {(selectedEvent.PatientName ?? selectedEvent.title ?? 'P').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[15px] font-bold text-[var(--v2-text-heading)] m-0 leading-snug truncate">
                                                {selectedEvent.PatientName ?? buildPatientDisplayName(selectedEvent)}
                                            </p>
                                            <p className="text-[11px] text-[var(--v2-text-muted)] m-0 font-mono">ID: {selectedEvent.PK}</p>
                                        </div>
                                        <div className="ml-auto flex-shrink-0 flex items-center gap-2">
                                            {statusBadge(selectedEvent.Status)}
                                            {isPathology && (
                                                <span
                                                    className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                                                    style={{ background: 'rgba(29,111,184,0.10)', color: '#1D6FB8', border: '1px solid rgba(29,111,184,0.20)' }}
                                                >
                                                    <FlaskConical size={10} strokeWidth={2} />
                                                    Pathology
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Grid of key details */}
                                    <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                                        <DetailItem
                                            label="Appointment Date & Time"
                                            value={moment.utc(selectedEvent.Date ?? selectedEvent.SK).local().format('MMM D, YYYY [at] hh:mm A')}
                                        />
                                        <DetailItem
                                            label="Appointment Type"
                                            value={
                                                <span className="capitalize font-bold" style={{ color: isPathology ? '#1D6FB8' : 'var(--v2-text-primary)' }}>
                                                    {apptType || '—'}
                                                </span>
                                            }
                                        />
                                        <DetailItem label="Scheduled On" value={moment(selectedEvent.CreatedAt).format('MMM D, YYYY [at] hh:mm A')} />
                                        <DetailItem label="Last Updated" value={moment(selectedEvent.UpdatedAt).format('MMM D, YYYY [at] hh:mm A')} />
                                    </div>
                                </div>
                            </section>

                            {/* ── Clinical Note ──────────────────────────────── */}
                            {selectedEvent.Note && (
                                <section>
                                    <p className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--v2-text-muted)' }}>
                                        Clinical Note
                                    </p>
                                    <div
                                        className="rounded-xl border px-4 py-3 flex items-start gap-3"
                                        style={{ borderColor: 'var(--v2-border-subtle)', background: 'var(--v2-bg-surface)' }}
                                    >
                                        <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--v2-text-muted)' }} />
                                        <p className="text-[13px] font-medium leading-relaxed m-0" style={{ color: 'var(--v2-text-primary)' }}>
                                            {selectedEvent.Note}
                                        </p>
                                    </div>
                                </section>
                            )}

                            {/* ── Lab Tests (pathology only) ─────────────────── */}
                            {tests.length > 0 && (
                                <section>
                                    <p className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--v2-text-muted)' }}>
                                        Lab Tests ({tests.length})
                                    </p>
                                    <div
                                        className="rounded-xl border overflow-hidden"
                                        style={{ borderColor: 'var(--v2-border-subtle)', background: 'var(--v2-bg-surface)' }}
                                    >
                                        {tests.map((t, i) => (
                                            <div
                                                key={t.testId}
                                                className="flex items-center gap-3 px-4 py-3 border-b last:border-0"
                                                style={{ borderColor: 'var(--v2-border-subtle)' }}
                                            >
                                                <span
                                                    className="w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black text-white flex-shrink-0"
                                                    style={{ background: '#059669' }}
                                                >
                                                    {i + 1}
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[13px] font-semibold m-0 truncate" style={{ color: 'var(--v2-text-primary)' }}>{t.testName}</p>
                                                    <p className="text-[10px] m-0 font-mono mt-0.5 truncate" style={{ color: 'var(--v2-text-muted)' }}>{t.testId}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* ── Pathology meta ─────────────────────────────── */}
                            {isPathology && (
                                <section>
                                    <p className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--v2-text-muted)' }}>
                                        Pathology Status
                                    </p>
                                    <div className="grid gap-4 rounded-xl border p-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', borderColor: 'var(--v2-border-subtle)', background: 'var(--v2-bg-surface)' }}>
                                        {slideStatus && (
                                            <DetailItem
                                                label="Slide Library Status"
                                                value={
                                                    <span
                                                        className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full"
                                                        style={{ background: 'rgba(29,111,184,0.08)', color: '#1D6FB8' }}
                                                    >
                                                        <Info size={11} strokeWidth={2} />
                                                        {slideStatus.replace(/_/g, ' ')}
                                                    </span>
                                                }
                                            />
                                        )}
                                        {linkedOrder && (
                                            <DetailItem
                                                label="Linked Lab Order"
                                                value={
                                                    <span className="inline-flex items-center gap-1.5 font-mono text-[12px] truncate" style={{ color: 'var(--v2-text-primary)' }}>
                                                        <Link2 size={11} strokeWidth={2} style={{ flexShrink: 0 }} />
                                                        {linkedOrder}
                                                    </span>
                                                }
                                            />
                                        )}
                                    </div>
                                </section>
                            )}

                            {/* ── Radiology / legacy examination details ─────── */}
                            {!isPathology && (
                                <section>
                                    <p className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--v2-text-muted)' }}>
                                        Examination Details
                                    </p>
                                    <div className="grid gap-4 rounded-xl border p-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', borderColor: 'var(--v2-border-subtle)', background: 'var(--v2-bg-surface)' }}>
                                        <DetailItem label="Examination Type" value={selectedEvent.TestType} />
                                        <DetailItem
                                            label="Study Area"
                                            value={Object.entries(selectedEvent.Data || {}).map(([k]: any) => (
                                                <div key={k} className="font-medium capitalize" style={{ color: 'var(--v2-text-primary)' }}>
                                                    {k.replace(/_/g, ' ')}
                                                </div>
                                            ))}
                                        />
                                    </div>
                                </section>
                            )}
                        </div>
                    );
                })()}
            </V2Modal>

            {/* ══════ EDIT — RADIOLOGY (existing AddScheduling form) ══════ */}
            <V2Modal
                isOpen={isAddSchedulingOpen}
                onClose={() => setIsAddSchedulingOpen(false)}
                size="xl"
                title="Update Appointment"
                description="Change appointment details and save."
                showCloseButton
            >
                <AddScheduling
                    modalPatient={selectedEvent}
                    toggleModal={setIsAddSchedulingOpen}
                    refreshAppointments={() => fetchAppointments(calendarDate, calendarView, apptTypeFilter)}
                />
            </V2Modal>

            {/* ══════ EDIT — PATHOLOGY (dedicated modal) ══════ */}
            {isPathologyEditOpen && selectedEvent && (
                <PathologyEditModal
                    event={selectedEvent}
                    onClose={() => setIsPathologyEditOpen(false)}
                    onSuccess={() => fetchAppointments(calendarDate, calendarView, apptTypeFilter)}
                />
            )}

            {showLabOrder && (
                <PlaceOrderModal
                    onClose={() => setShowLabOrder(false)}
                    onSuccess={() => setShowLabOrder(false)}
                />
            )}
        </div>
    );
}
